import { Chip } from '@mui/material'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { useAuth } from '../../provider/OIDCProvider'
import useAppSettings from '../../../lib/hooks/useAppSettings'
import useRoleSwitch from '../../../lib/hooks/useRoleSwitch'
import useShowRoleSwitcher from '../../../lib/hooks/useShowRoleSwitcher'
import { ActiveRole } from '../../../lib/activeRole'

/**
 * Persona switcher for dual-role (student + advisor) users. Shows the current
 * view and switches to the other persona on click. Renders nothing for
 * single-role users or when the institution's DUAL_ROLE_SWITCHER feature flag
 * is off.
 */
const RoleSwitcher = () => {
  const { activeRole } = useAuth()
  const { text } = useAppSettings()
  const { switchRole } = useRoleSwitch()
  const show = useShowRoleSwitcher()

  if (!show || !activeRole) {
    return null
  }

  // Derive the advisor label from the configurable term (e.g. "Success Coach")
  // rather than hardcoding "Advising".
  const roleLabels: Record<ActiveRole, string> = {
    student: 'Student view',
    advisor: `${text?.advisor || 'Advising'} view`
  }

  const next: ActiveRole = activeRole === 'advisor' ? 'student' : 'advisor'

  return (
    <Chip
      icon={<SwapHorizIcon />}
      label={roleLabels[activeRole]}
      onClick={() => switchRole(next)}
      aria-label={`Current view: ${roleLabels[activeRole]}. Switch to ${roleLabels[next]}`}
      size="small"
      variant="outlined"
      // The top-nav is a flex row; without this the chip shrinks and its label
      // ellipsis-truncates (e.g. "A…"). Keep it at its natural width.
      sx={{ flexShrink: 0, maxWidth: 'none' }}
    />
  )
}

export default RoleSwitcher
